import { Icon } from "@iconify/react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import io from "socket.io-client";
import { toast } from "react-toastify";
import ClipLoader from "react-spinners/ClipLoader";
import WebRTCListenerClient from "../utils/webrtc/listenerClient";
import {
  fetchEventImage,
  fetchSpecificEvent,
  fetchUsers,
  fetchUsersImage,
  addComment,
  fetchComments,
  clearComments,
  addReaction,
  removeReaction,
  fetchReactionCounts,
  receiveComment,
  receiveReactionAdd,
  receiveReactionRemove,
  fetchSpecificChannel,
  checkCallBlockStatus,
  checkChatBlockStatus,
  flagComment,
  unflagComment,
  fetchCommentFlags,
} from "../redux/slices/userSlice";

const SIGNALING_URL = "wss://telecoms.pluglr.com";
const STREAM_TOKEN_URL =
  "https://apis.pluglr.com/pluglr/api/v1/auth/resolve/stream";
const API_BASE_URL = "https://apis.pluglr.com/pluglr";
const MAX_CONNECTION_RETRIES = 10;
const RETRY_DELAY_MS = 2000;
const MODERATION_ACTIONS = [
  "APPROVE_MESSAGE",
  "REJECT_MESSAGE",
  "BLOCK_USER_FROM_CHAT",
  "BLOCK_USER_FROM_CALLS",
  "KICKOUT_FROM_EVENT",
  "KICKOUT_FROM_CHANNEL",
  "NOT_SET",
];

const sanitizeToken = (storedToken) => {
  if (!storedToken || typeof storedToken !== "string") {
    return null;
  }

  let tokenString = storedToken;
  try {
    tokenString = decodeURIComponent(tokenString);
  } catch (err) {
    // swallow decode errors – we'll fall back to the raw value
  }

  const primarySegment = tokenString.split("&")[0] ?? tokenString;
  const withoutBearer = primarySegment.replace(/^Bearer(?:%20|\s)+/i, "");
  const trimmed = withoutBearer.trim();

  return trimmed.length > 0 ? trimmed : null;
};

const defaultListener = {
  id: null,
  email: undefined,
};

const UserListener = () => {
  const { channelId, eventId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    eventImages,
    userData,
    userImage,
    specificEvent,
    comments,
    commentsLoading,
    commentsHasMore,
    commentsPage,
    userChannel,
  } = useSelector((state) => state.userData);

  console.log(userChannel);

  const audioRef = useRef(null);
  const monitorAudioRef = useRef(null);
  const listenerRef = useRef(null);
  const socketRef = useRef(null);
  const mountedRef = useRef(false);
  const streamTokenRef = useRef(null);
  const streamTokenPromiseRef = useRef(null);
  const retryAttemptsRef = useRef(0);
  const retryTimeoutRef = useRef(null);
  const retryScheduledRef = useRef(false);
  const startListenerRef = useRef(null);
  const chatContainerRef = useRef(null);

  const [status, setStatus] = useState("Preparing stream");
  const [error, setError] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [stationName, setStationName] = useState("Pluglr Radio");
  const [streamTitle, setStreamTitle] = useState("Live stream");
  const [streamDescription, setStreamDescription] = useState(
    "Streamin from pluglr"
  );
  const [isExpanded, setIsExpanded] = useState(false);
  const [callState, setCallState] = useState({ status: "idle", message: "" });
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [audienceLevel, setAudienceLevel] = useState(100);
  const [audienceMuted, setAudienceMuted] = useState(false);
  const [selfMonitorEnabled, setSelfMonitorEnabled] = useState(false);
  const [selfMonitorLevel, setSelfMonitorLevel] = useState(50);
  const [audioDevices, setAudioDevices] = useState({ inputs: [], outputs: [] });
  const [selectedMicId, setSelectedMicId] = useState("default");
  const [selectedSpeakerId, setSelectedSpeakerId] = useState("default");
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [deviceError, setDeviceError] = useState(null);
  const [speakerError, setSpeakerError] = useState(null);
  const [panelTab, setPanelTab] = useState("controls");
  const [commentText, setCommentText] = useState("");
  const [isChannelModerator, setIsChannelModerator] = useState(false);
  const [moderationSelections, setModerationSelections] = useState({});
  const [moderationReasons, setModerationReasons] = useState({});
  const [moderationLoading, setModerationLoading] = useState({});
  const [openModerationCommentId, setOpenModerationCommentId] = useState(null);
  const [moderationSuccessMessage, setModerationSuccessMessage] =
    useState(null);
  const [moderationErrorMessage, setModerationErrorMessage] = useState(null);

  // Flagging State
  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const [flaggingCommentId, setFlaggingCommentId] = useState(null);
  const [flagType, setFlagType] = useState("HARASSMENT_AND_BULLYING"); // Fixed typo
  const [specificReason, setSpecificReason] = useState("");
  const [flagLoading, setFlagLoading] = useState(false);

  // Moderator View Flags State
  const [viewFlagsModalOpen, setViewFlagsModalOpen] = useState(false);
  const [viewFlagsCommentId, setViewFlagsCommentId] = useState(null);
  const [commentFlags, setCommentFlags] = useState([]);
  const [flagsLoading, setFlagsLoading] = useState(false);

  const canSelectSpeaker = useMemo(() => {
    if (typeof document === "undefined") return false;
    const audio = document.createElement("audio");
    return typeof audio.setSinkId === "function";
  }, []);

  const fetchAudioDevices = useCallback(async () => {
    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices?.enumerateDevices
    ) {
      setDeviceError(
        "Audio device selection is not supported in this browser."
      );
      setAudioDevices({ inputs: [], outputs: [] });
      return;
    }

    setDevicesLoading(true);
    setDeviceError(null);
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const inputs = [];
      const outputs = [];
      let micCounter = 1;
      let speakerCounter = 1;

      devices.forEach((device) => {
        if (device.kind === "audioinput" && device.deviceId !== "default") {
          inputs.push({
            deviceId: device.deviceId,
            label: device.label || `Microphone ${micCounter++}`,
          });
        }
        if (device.kind === "audiooutput" && device.deviceId !== "default") {
          outputs.push({
            deviceId: device.deviceId,
            label: device.label || `Speaker ${speakerCounter++}`,
          });
        }
      });

      setAudioDevices({ inputs, outputs });

      setSelectedMicId((prev) => {
        if (prev === "default") return prev;
        return inputs.some((device) => device.deviceId === prev)
          ? prev
          : "default";
      });

      setSelectedSpeakerId((prev) => {
        if (prev === "default") return prev;
        return outputs.some((device) => device.deviceId === prev)
          ? prev
          : "default";
      });
    } catch (err) {
      console.warn("enumerateDevices failed", err);
      setDeviceError(err?.message || "Unable to list audio devices.");
    } finally {
      setDevicesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAudioDevices();
    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices?.addEventListener
    ) {
      return undefined;
    }
    const handleDeviceChange = () => {
      fetchAudioDevices();
    };
    navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener(
        "devicechange",
        handleDeviceChange
      );
    };
  }, [fetchAudioDevices]);

  // console.log(eventData);

  const userToken = useMemo(() => localStorage.getItem("pluglrUserToken"), []);
  const authHeader = useMemo(() => {
    if (!userToken) return null;
    return userToken.startsWith("Bearer ") ? userToken : `Bearer ${userToken}`;
  }, [userToken]);

  const resolvedUserDetails = useMemo(() => {
    const storageKeys = ["pluglrUserDetails", "pluglrUserProfile"];

    for (const key of storageKeys) {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          return parsed;
        }
      } catch (err) {
        console.warn(`parse ${key}`, err);
      }
    }

    return null;
  }, []);

  const listenerIdentity = useMemo(() => {
    if (!resolvedUserDetails) {
      return {
        id: null,
        email: undefined,
        name: undefined,
        displayName: undefined,
      };
    }

    const idCandidate =
      resolvedUserDetails.id ||
      resolvedUserDetails._id ||
      resolvedUserDetails.userId ||
      resolvedUserDetails.uuid ||
      null;

    const composedName = [resolvedUserDetails.fname, resolvedUserDetails.lname]
      .filter(Boolean)
      .join(" ");
    const displayName =
      composedName ||
      resolvedUserDetails.name ||
      resolvedUserDetails.displayName ||
      resolvedUserDetails.username ||
      undefined;

    return {
      id: idCandidate || null,
      email: resolvedUserDetails.email || undefined,
      name: displayName,
      displayName,
    };
  }, [resolvedUserDetails]);

  const currentUserId = useMemo(() => {
    return userData?.data?.id || listenerIdentity?.id || null;
  }, [userData, listenerIdentity]);

  useEffect(() => {
    if (!channelId || !eventId) {
      toast.error("Stream details are missing.");
      navigate(-1);
      return;
    }

    if (!userToken) {
      toast.error("You must be logged in to join this stream.");
      navigate("/login/listener");
    }
  }, [channelId, eventId, navigate, userToken]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;

      if (listenerRef.current) {
        listenerRef.current
          .cleanup()
          .catch((err) => console.warn("listener cleanup", err));
        listenerRef.current = null;
      }

      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }

      streamTokenRef.current = null;
      streamTokenPromiseRef.current = null;

      const audio = audioRef.current;
      if (audio) {
        try {
          audio.pause();
          audio.srcObject = null;
        } catch (err) {
          console.warn("audio cleanup", err);
        }
      }
    };
  }, []);

  useEffect(() => {
    if (!eventId) return;
    dispatch(fetchSpecificEvent(eventId));
    if (!eventImages?.[eventId]) {
      dispatch(fetchEventImage(eventId));
    }
  }, [dispatch, eventId, eventImages]);

  useEffect(() => {
    if (channelId) {
      dispatch(fetchSpecificChannel(channelId));
    }
  }, [dispatch, channelId]);

  useEffect(() => {
    const eventData = specificEvent?.data;
    console.log(eventData);

    if (!eventData) return;

    if (eventData.title) {
      setStreamTitle(eventData.title);
    }
    if (eventData.description) {
      setStreamDescription(eventData.description);
    }
    if (eventData.channel?.name) {
      setStationName(eventData.channel.name);
    }
  }, [specificEvent]);

  useEffect(() => {
    const channelData =
      userChannel?.data || userChannel?.channel || userChannel;

    if (channelData?.name) {
      setStationName(channelData.name);
    }
    if (channelData?.description && !specificEvent?.data?.description) {
      setStreamDescription(channelData.description);
    }
  }, [userChannel, specificEvent]);

  useEffect(() => {
    const moderators =
      userChannel?.data?.moderators || userChannel?.moderators || [];

    if (!Array.isArray(moderators) || !currentUserId) {
      setIsChannelModerator(false);
      return;
    }

    const isModerator = moderators.some(
      (moderator) => String(moderator?.userId) === String(currentUserId)
    );
    setIsChannelModerator(isModerator);
    if (!isModerator) {
      setOpenModerationCommentId(null);
    }
  }, [userChannel, currentUserId]);

  const defaultEventImage = "/images/audio-live-bg.png";
  const eventBackgroundImage = eventImages?.[eventId] ?? defaultEventImage;

  const middleAudioBackground = useMemo(
    () =>
      `linear-gradient(
        180deg,
        rgba(0, 0, 0, 0) 32%,
        rgba(0, 0, 0, 0.25) 55%,
        rgba(0, 0, 0, 0.55) 75%,
        rgba(0, 0, 0, 0.92) 100%
      ),
      linear-gradient(0deg, rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0.35)),
      url('${eventBackgroundImage}')`,
    [eventBackgroundImage]
  );

  const resolveStreamToken = useCallback(() => {
    if (streamTokenRef.current) {
      return Promise.resolve(streamTokenRef.current);
    }

    if (streamTokenPromiseRef.current) {
      return streamTokenPromiseRef.current;
    }

    if (!channelId || !eventId) {
      return Promise.reject(new Error("Stream details are missing."));
    }

    if (!userToken) {
      return Promise.reject(
        new Error("Authentication required to resolve stream token.")
      );
    }

    const promise = (async () => {
      try {
        const response = await fetch(
          `${STREAM_TOKEN_URL}/${channelId}/${eventId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `${userToken}`,
              "X-From": "web",
            },
          }
        );

        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          const message = payload?.message || "Unable to resolve stream token";
          throw new Error(message);
        }

        const payload = await response.json();
        const streamToken = payload?.data?.token;

        if (!streamToken) {
          throw new Error("Invalid stream token response from server");
        }

        streamTokenRef.current = streamToken;
        return streamToken;
      } catch (error) {
        streamTokenRef.current = null;
        throw error;
      } finally {
        streamTokenPromiseRef.current = null;
      }
    })();

    streamTokenPromiseRef.current = promise;
    return promise;
  }, [channelId, eventId, userToken]);

  const connectSocket = useCallback(
    (streamToken, handlers = {}) =>
      new Promise((resolve, reject) => {
        if (!streamToken) {
          reject(new Error("Stream token missing"));
          return;
        }

        try {
          const socket = io(SIGNALING_URL, {
            path: "/socket.io",
            transports: ["websocket"],
            query: {
              token: streamToken,
              channelId,
              eventId,
              role: "listener",
            },
          });

          socket.once("connect", () => resolve(socket));
          socket.once("connect_error", (err) => {
            handlers.onConnectError?.(err);
            reject(err || new Error("Socket connection failed"));
          });

          if (handlers.onDisconnect) {
            socket.on("disconnect", handlers.onDisconnect);
          }

          // Real-time Chat & Reactions
          // Remove potential duplicate listeners first
          socket.off("onComment");
          socket.off("onReactionAdd");
          socket.off("onReactionRemove");

          socket.on("onComment", (comment) => {
            console.log("Received real-time comment:", comment);
            dispatch(receiveComment(comment));
          });

          socket.on("onReactionAdd", (data) => {
            console.log("UserListener: Received real-time reactionAdd:", data);
            dispatch(receiveReactionAdd(data));
          });

          socket.on("onReactionRemove", (data) => {
            console.log("Received real-time reactionRemove:", data);
            dispatch(receiveReactionRemove(data));
          });

          socketRef.current = socket;
        } catch (err) {
          reject(err);
        }
      }),
    [channelId, eventId]
  );

  const clearRetryTimer = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, []);

  const cleanupConnections = useCallback(() => {
    const activeListener = listenerRef.current;
    if (activeListener) {
      listenerRef.current = null;
      activeListener
        .cleanup()
        .catch((err) => console.warn("listener cleanup", err));
    }

    const activeSocket = socketRef.current;
    if (activeSocket) {
      activeSocket.removeAllListeners?.();
      activeSocket.disconnect();
      socketRef.current = null;
    }

    if (audioRef.current) {
      try {
        audioRef.current.srcObject = null;
      } catch (err) {
        console.warn("audio cleanup", err);
      }
    }
  }, []);

  const scheduleRetry = useCallback(
    (reason) => {
      if (!mountedRef.current) return;

      cleanupConnections();
      clearRetryTimer();

      if (retryScheduledRef.current) {
        const message = reason || "Reconnecting…";
        setError(message);
        toast.warn(message);
        return;
      }

      const nextAttempt = retryAttemptsRef.current + 1;
      if (nextAttempt > MAX_CONNECTION_RETRIES) {
        retryAttemptsRef.current = nextAttempt;
        retryScheduledRef.current = false;
        const finalMessage =
          reason ||
          "Unable to connect to the stream. Please check your connection and try again.";
        setError(finalMessage);
        setStatus("Disconnected");
        toast.error(finalMessage);
        window.alert(finalMessage);
        return;
      }

      retryAttemptsRef.current = nextAttempt;
      retryScheduledRef.current = true;

      const reconnectMessage = reason || "Attempting to reconnect…";
      setError(reconnectMessage);
      setStatus(
        `Reconnecting… (attempt ${nextAttempt} of ${MAX_CONNECTION_RETRIES})`
      );
      toast.warn(reconnectMessage);

      retryTimeoutRef.current = window.setTimeout(() => {
        retryTimeoutRef.current = null;
        retryScheduledRef.current = false;
        if (!mountedRef.current) return;
        startListenerRef.current?.();
      }, RETRY_DELAY_MS);
    },
    [cleanupConnections, clearRetryTimer]
  );

  const handleSocketDisconnect = useCallback(
    (reason) => {
      if (!mountedRef.current) return;
      const detail =
        typeof reason === "string" && reason.length > 0
          ? `Connection lost: ${reason}`
          : "Connection lost.";
      scheduleRetry(detail);
    },
    [scheduleRetry]
  );

  const handleSocketError = useCallback(
    (err) => {
      if (!mountedRef.current) return;
      const message = err?.message || "Socket connection failed";
      scheduleRetry(message);
    },
    [scheduleRetry]
  );

  const attemptMonitorPlayback = useCallback(() => {
    const monitor = monitorAudioRef.current;
    if (!monitor || !monitor.srcObject) return;
    if (!monitor.paused) return;
    monitor.play().catch((err) => {
      console.warn("Monitor playback attempt failed", err);
    });
  }, []);

  const attemptPlayback = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return Promise.resolve(false);

    return audio
      .play()
      .then(() => {
        if (!mountedRef.current) return true;
        setIsPlaying(true);
        setIsMuted(audio.muted);
        setAutoplayBlocked(false);
        return true;
      })
      .catch((err) => {
        console.warn("Audio playback attempt failed", err);
        if (!mountedRef.current) return false;
        setIsReady(true);
        setIsPlaying(false);
        setIsMuted(audio.muted);
        setAutoplayBlocked(true);
        return false;
      });
  }, []);

  const onSpeaking = useCallback((speaking) => {
    if (!mountedRef.current) return;
    const audio = audioRef.current;
    if (audio) {
      // Duck volume to 20% when speaking, restore to 100% when silent
      const targetVolume = speaking ? 0.2 : 1.0;
      audio.volume = targetVolume;
    }
  }, []);

  const startListener = useCallback(async () => {
    if (!mountedRef.current) return;

    if (!channelId || !eventId) {
      toast.error("Invalid streaming details");
      return;
    }

    if (!userToken) {
      toast.error("Authentication required to join stream");
      return;
    }

    clearRetryTimer();
    retryScheduledRef.current = false;

    cleanupConnections();

    setStatus("Connecting to stream");
    setError(null);

    try {
      const streamToken = await resolveStreamToken();
      const socket = await connectSocket(streamToken, {
        onDisconnect: handleSocketDisconnect,
        onConnectError: handleSocketError,
      });

      socketRef.current = socket;

      const listener = new WebRTCListenerClient({
        socket,
        channelId,
        eventId,
        user: { ...defaultListener, ...listenerIdentity, role: "listener" },
        role: "listener",
        useSocketIdForPeer: false,
        onStatusChange: (message) => {
          if (!mountedRef.current) return;
          setStatus(message);
        },
        onError: (err) => {
          if (!mountedRef.current) return;
          const msg = err?.message || "Listener error";
          console.error("Listener error", err);
          setError(msg);
          setStatus("Encountered an error");
          scheduleRetry(msg);
        },
        onTrack: (stream) => {
          if (!mountedRef.current) return;
          const audio = audioRef.current;
          if (!audio) return;

          audio.srcObject = stream;
          setIsReady(true);
          attemptPlayback();
        },
        onCallStateChange: (state) => {
          if (!mountedRef.current) return;
          setCallState(state || { status: "idle", message: "" });
        },
        onSpeaking,
        onRoleChange: (role) => {
          if (!mountedRef.current) return;
          setListenerRole(role || "listener");
        },
      });

      listenerRef.current = listener;

      await listener.start();

      if (!mountedRef.current) {
        return;
      }

      retryAttemptsRef.current = 0;
      retryScheduledRef.current = false;
      setStatus("Listening");
      setError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error("Failed to start listener", err);
      if (!mountedRef.current) return;
      scheduleRetry(message);
    }
  }, [
    attemptPlayback,
    channelId,
    connectSocket,
    eventId,
    handleSocketDisconnect,
    handleSocketError,
    listenerIdentity,
    resolveStreamToken,
    scheduleRetry,
    userToken,
    clearRetryTimer,
    cleanupConnections,
    onSpeaking,
  ]);

  useEffect(() => {
    startListenerRef.current = startListener;
  }, [startListener]);

  useEffect(() => {
    startListener();

    return () => {
      clearRetryTimer();
      retryScheduledRef.current = false;
      retryAttemptsRef.current = 0;
      startListenerRef.current = null;
      cleanupConnections();
    };
  }, [startListener, clearRetryTimer, cleanupConnections]);

  console.log(userData);

  useEffect(() => {
    dispatch(fetchUsersImage());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  const handleTogglePlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      attemptPlayback();
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  };

  const handleModerationSelectionChange = (commentId, action) => {
    setModerationSelections((prev) => ({
      ...prev,
      [commentId]: action,
    }));
  };

  const handleModerationReasonChange = (commentId, value) => {
    setModerationReasons((prev) => ({
      ...prev,
      [commentId]: value,
    }));
  };

  const handleToggleModerationMenu = (commentId) => {
    setOpenModerationCommentId((prev) =>
      prev === commentId ? null : commentId
    );
  };

  const handleModerationSubmit = async (comment) => {
    if (!isChannelModerator) return;
    const selectedAction =
      moderationSelections[comment.id] ||
      moderationSelections[comment._id] ||
      "NOT_SET";

    if (!eventId) {
      toast.error("Event information is missing for moderation.");
      return;
    }

    if (!authHeader) {
      toast.error("Authentication required to moderate.");
      return;
    }

    if (selectedAction === "NOT_SET") {
      toast.error("Select a moderation action first.");
      return;
    }

    const moderatedUserId =
      comment.userId || comment.user?.id || comment.user?.userId;

    if (!moderatedUserId) {
      toast.error("Unable to determine the user to moderate.");
      return;
    }

    const commentId = comment.id || comment._id;
    if (!commentId) {
      toast.error("Comment identifier missing.");
      return;
    }

    const reason = moderationReasons[commentId]?.trim() || "Moderator action";

    setModerationLoading((prev) => ({
      ...prev,
      [commentId]: true,
    }));

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/auth/channels/moderation/moderate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: authHeader,
            "X-From": "web",
          },
          body: JSON.stringify({
            eventId,
            moderatedUserId,
            commentId,
            reason,
            moderationAction: selectedAction,
          }),
        }
      );

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          payload?.message || "Failed to apply moderation action."
        );
      }

      toast.success(payload?.message || "Moderation action applied.");
      setModerationSuccessMessage(
        payload?.message || "Moderation action applied successfully."
      );
      setModerationSelections((prev) => ({
        ...prev,
        [commentId]: "NOT_SET",
      }));
      setModerationReasons((prev) => ({
        ...prev,
        [commentId]: "",
      }));
      setOpenModerationCommentId((prev) => (prev === commentId ? null : prev));
    } catch (err) {
      toast.error(err.message || "Unable to apply moderation action.");
      setModerationErrorMessage(
        err.message || "Unable to apply moderation action."
      );
    } finally {
      setModerationLoading((prev) => ({
        ...prev,
        [commentId]: false,
      }));
    }
  };

  const handleMuteToggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    const nextMuted = !audio.muted;
    audio.muted = nextMuted;
    setIsMuted(nextMuted);
  };

  const handleAudienceVolumeChange = useCallback((event) => {
    const value = Number(event.target.value);
    if (!Number.isFinite(value)) return;
    setAudienceLevel(Math.max(0, Math.min(100, value)));
  }, []);

  const handleAudienceMuteToggle = useCallback(() => {
    setAudienceMuted((prev) => !prev);
  }, []);

  const handleSelfMonitorToggle = useCallback(() => {
    setSelfMonitorEnabled((prev) => !prev);
  }, []);

  const handleSelfMonitorVolumeChange = useCallback((event) => {
    const value = Number(event.target.value);
    if (!Number.isFinite(value)) return;
    setSelfMonitorLevel(Math.max(0, Math.min(100, value)));
  }, []);

  const handleMicSelect = useCallback((event) => {
    setSelectedMicId(event.target.value);
  }, []);

  const handleSpeakerSelect = useCallback((event) => {
    setSelectedSpeakerId(event.target.value);
  }, []);

  const handleRefreshDevices = useCallback(() => {
    fetchAudioDevices();
  }, [fetchAudioDevices]);

  const handleEndCall = useCallback(() => {
    try {
      listenerRef.current?.endCall();
    } catch (err) {
      console.warn("endCall failed", err);
    }
  }, []);

  const handleRetry = useCallback(() => {
    clearRetryTimer();
    retryScheduledRef.current = false;
    retryAttemptsRef.current = 0;
    cleanupConnections();
    streamTokenRef.current = null;
    streamTokenPromiseRef.current = null;
    startListenerRef.current?.();
  }, [cleanupConnections, clearRetryTimer]);

  const [listenerRole, setListenerRole] = useState("listener");
  const isCohost = listenerRole === "cohost";

  const statusVariant = error ? "error" : isPlaying ? "success" : "info";
  const liveLabel = statusVariant === "success" ? "Live" : "Connecting";
  const pillLabel =
    statusVariant === "success" ? "Live broadcast" : "Preparing stream";
  const expandToggleLabel = isExpanded ? "Collapse preview" : "Expand preview";
  const expandToggleIcon = isExpanded
    ? "mdi:fullscreen-exit"
    : "mdi:fullscreen";
  const playButtonLabel = isPlaying
    ? "Pause stream"
    : isReady
    ? "Play stream"
    : "Connecting";
  const canControlPlayback = isReady || isPlaying;

  const viewerName = useMemo(() => {
    if (listenerIdentity.displayName) return listenerIdentity.displayName;
    if (listenerIdentity.name) return listenerIdentity.name;
    if (listenerIdentity.email) return listenerIdentity.email;

    try {
      const stored = localStorage.getItem("pluglrUserDetails");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.fname || parsed?.lname) {
          return (
            [parsed.fname, parsed.lname].filter(Boolean).join(" ") ||
            parsed.email
          );
        }
        if (parsed?.name) {
          return parsed.name;
        }
        if (parsed?.email) {
          return parsed.email;
        }
      }
    } catch (err) {
      console.warn("parse user details", err);
    }

    try {
      const fallback = localStorage.getItem("pluglrUserProfile");
      if (fallback) {
        const parsed = JSON.parse(fallback);
        if (parsed?.name) return parsed.name;
        if (parsed?.email) return parsed.email;
      }
    } catch (err) {
      console.warn("parse user profile", err);
    }

    return "Listener";
  }, [listenerIdentity]);

  const handleRequestCall = useCallback(async () => {
    if (!listenerRef.current) {
      toast.error("Connection not ready yet");
      return;
    }

    try {
      const userId = userData?.id || userData?.data?.id;
      if (userId && eventId) {
        try {
          const result = await dispatch(
            checkCallBlockStatus({ eventId, userId })
          ).unwrap();

          // Check if API returns success but data is true (user is blocked)
          if (
            result?.data === true ||
            result?.message === "User blocked: true"
          ) {
            setModerationErrorMessage("You are blocked from making calls.");
            return;
          }
        } catch (blockErr) {
          // Check for 400 status or specific message
          if (
            blockErr?.status === 400 ||
            blockErr?.statusCode === 400 ||
            blockErr?.message?.includes("blocked")
          ) {
            setModerationErrorMessage("You are blocked from making calls.");
            return;
          }
        }
      }

      await listenerRef.current.requestCall(viewerName);
      toast.info("Call request sent");
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.error(message);
    }
  }, [viewerName, userData, eventId, dispatch]);

  const isRequestingCall = callState.status === "requesting";
  const callStatusMessage = callState.message || "";
  const isCallLive = useMemo(
    () => ["connected", "producing"].includes(callState.status),
    [callState.status]
  );
  const showCallControls = useMemo(
    () =>
      !isCohost &&
      ["promoted", "capturing", "connected", "producing"].includes(
        callState.status
      ),
    [callState.status, isCohost]
  );
  const [callControlsOpen, setCallControlsOpen] = useState(false);

  useEffect(() => {
    if (!showCallControls) {
      setCallControlsOpen(false);
    }
  }, [showCallControls]);

  const handleCloseCallControls = useCallback(() => {
    setCallControlsOpen(false);
  }, []);

  useEffect(() => {
    if (!callControlsOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        handleCloseCallControls();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [callControlsOpen, handleCloseCallControls]);

  const callButtonLabel = useMemo(() => {
    if (isCallLive) return "End call";
    if (isRequestingCall) return "Requesting…";
    return "Call the host";
  }, [isCallLive, isRequestingCall]);

  const callButtonIcon = isCallLive ? "mdi:phone-hangup" : "mdi:phone";

  const handleCallAction = useCallback(() => {
    if (isCallLive) {
      handleEndCall();
      return;
    }
    handleRequestCall();
  }, [handleEndCall, handleRequestCall, isCallLive]);

  const callButtonDisabled = isCallLive
    ? false
    : isRequestingCall || !listenerRef.current;

  const handleOpenCallControls = useCallback(() => {
    if (!showCallControls) return;
    setCallControlsOpen(true);
  }, [showCallControls]);

  useEffect(() => {
    if (!autoplayBlocked) return undefined;

    const handleUserGesture = () => {
      attemptPlayback().then((played) => {
        if (played) {
          window.removeEventListener("pointerdown", handleUserGesture);
          window.removeEventListener("keydown", handleUserGesture);
        }
      });
    };

    window.addEventListener("pointerdown", handleUserGesture, { once: true });
    window.addEventListener("keydown", handleUserGesture, { once: true });

    return () => {
      window.removeEventListener("pointerdown", handleUserGesture);
      window.removeEventListener("keydown", handleUserGesture);
    };
  }, [autoplayBlocked, attemptPlayback]);

  useEffect(() => {
    const listener = listenerRef.current;
    if (!listener) return;
    const normalized = audienceLevel / 100;
    listener.setAudienceVolume(normalized);
  }, [audienceLevel]);

  useEffect(() => {
    const listener = listenerRef.current;
    if (!listener) return;
    listener.setAudienceMuted(audienceMuted);
  }, [audienceMuted]);

  useEffect(() => {
    const listener = listenerRef.current;
    if (!listener) return;
    const normalized = selfMonitorLevel / 100;
    listener.setSelfMonitorVolume(normalized);
    if (monitorAudioRef.current) {
      monitorAudioRef.current.volume = normalized;
    }
  }, [selfMonitorLevel]);

  useEffect(() => {
    const listener = listenerRef.current;
    if (!listener) return;
    listener.setSelfMonitorEnabled(selfMonitorEnabled);
    const monitor = monitorAudioRef.current;
    if (!monitor) return;
    monitor.muted = !selfMonitorEnabled;
    if (selfMonitorEnabled) {
      attemptMonitorPlayback();
    } else {
      try {
        monitor.pause();
      } catch (err) {
        console.warn("Pause monitor playback", err);
      }
    }
  }, [selfMonitorEnabled, attemptMonitorPlayback]);

  useEffect(() => {
    const listener = listenerRef.current;
    const monitor = monitorAudioRef.current;
    if (!monitor) return;
    const stream = listener?.getMonitorStream?.();
    if (stream) {
      if (monitor.srcObject !== stream) {
        monitor.srcObject = stream;
      }
      if (selfMonitorEnabled) {
        attemptMonitorPlayback();
      }
    } else if (monitor.srcObject) {
      monitor.srcObject = null;
    }
  }, [callState.status, selfMonitorEnabled, attemptMonitorPlayback]);

  useEffect(() => {
    if (callState.status === "ended" || callState.status === "idle") {
      setSelfMonitorEnabled(false);
      setAudienceMuted(false);
    }
  }, [callState.status]);

  useEffect(() => {
    const listener = listenerRef.current;
    if (!listener || typeof listener.setAudioInputDevice !== "function") {
      return;
    }
    const normalized = selectedMicId === "default" ? null : selectedMicId;
    listener.setAudioInputDevice(normalized);
  }, [selectedMicId]);

  useEffect(() => {
    if (!canSelectSpeaker) {
      return;
    }
    const targets = [audioRef.current, monitorAudioRef.current].filter(
      (element) => element && typeof element.setSinkId === "function"
    );
    if (!targets.length) {
      return;
    }
    const sinkId =
      selectedSpeakerId === "default" ? "default" : selectedSpeakerId;
    let cancelled = false;
    const applySink = async () => {
      setSpeakerError(null);
      for (const element of targets) {
        try {
          await element.setSinkId(sinkId);
        } catch (err) {
          if (cancelled) {
            return;
          }
          console.warn("setSinkId failed", err);
          setSpeakerError(err?.message || "Unable to switch playback device.");
          break;
        }
      }
    };
    applySink();
    return () => {
      cancelled = true;
    };
  }, [selectedSpeakerId, canSelectSpeaker]);

  // Fetch comments when chat tab is active
  useEffect(() => {
    if (panelTab === "chat" && eventId) {
      if ((comments?.content?.length || 0) === 0) {
        dispatch(fetchComments({ eventId, page: 0 }));
      }
    }
  }, [panelTab, eventId, dispatch, comments?.content?.length]);

  const handleChatScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 50 &&
      !commentsLoading &&
      commentsHasMore
    ) {
      dispatch(
        fetchComments({
          eventId,
          page: commentsPage + 1,
        })
      );
    }
  };

  const handleSendComment = async () => {
    if (!commentText.trim()) return;
    if (!eventId) return;

    try {
      const userId = userData?.id || userData?.data?.id;
      if (userId) {
        try {
          const result = await dispatch(
            checkChatBlockStatus({ eventId, userId })
          ).unwrap();

          // Check if API returns success but data is true (user is blocked)
          if (
            result?.data === true ||
            result?.message === "User blocked: true"
          ) {
            setModerationErrorMessage("You are blocked from sending messages.");
            return;
          }
        } catch (blockErr) {
          // Check for 400 status or specific message
          if (
            blockErr?.status === 400 ||
            blockErr?.statusCode === 400 ||
            blockErr?.message?.includes("blocked")
          ) {
            setModerationErrorMessage("You are blocked from sending messages.");
            return;
          }
        }
      }
    } catch (err) {
      console.warn("Block check failed", err);
    }

    const result = await dispatch(addComment({ eventId, text: commentText }));

    if (!result.error) {
      setCommentText("");

      // Emit to socket for real-time broadcast
      if (socketRef.current) {
        // Use result.payload.data if nested, otherwise result.payload
        const socketPayload = result.payload?.data || result.payload;
        socketRef.current.emit("comment", socketPayload, (err) => {
          if (err) console.error("Error broadcasting comment:", err);
          else console.log("Comment broadcasted via socket");
        });
      }

      dispatch(clearComments());
      dispatch(fetchComments({ eventId, page: 0 }));
    }
  };

  const handleReaction = async (commentId, reaction, currentReaction) => {
    try {
      let result;
      if (reaction === "NOT_SET" || reaction === currentReaction) {
        // Explicitly clear or toggle off the current reaction
        result = await dispatch(removeReaction(commentId)).unwrap();
        if (socketRef.current) {
          const userId = userData?.id || userData?.data?.id;
          socketRef.current.emit(
            "reactionRemove",
            { commentId, reactionType: currentReaction, userId },
            (err) => {
              if (err) {
                console.error(
                  "error while broadcasting reactionRemove:",
                  JSON.stringify(err)
                );
              } else {
                console.log("comment broadcasted (reaction removed)");
              }
            }
          );
        }
      } else {
        // Set new reaction (backend should handle removing previous reaction)
        result = await dispatch(addReaction({ commentId, reaction })).unwrap();
        if (socketRef.current) {
          const userId = userData?.id || userData?.data?.id;
          socketRef.current.emit(
            "reactionAdd",
            {
              commentId,
              reactionType: reaction,
              previousReactionType: currentReaction || null,
              userId,
            },
            (err) => {
              if (err) {
                console.error(
                  "error while broadcasting reactionAdd:",
                  JSON.stringify(err)
                );
              } else {
                console.log("comment broadcasted (reaction added)");
              }
            }
          );
        }
      }
    } catch (err) {
      console.error("Failed to handle reaction:", err);
    }
  };

  function timeAgo(date) {
    if (!date) return "";
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return "just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hr ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? "s" : ""} ago`;
  }

  // Flagging Handlers
  const handleOpenFlagModal = (commentId) => {
    setFlaggingCommentId(commentId);
    setFlagType("HARASSMENT_AND_BULLYING");
    setSpecificReason("");
    setFlagModalOpen(true);
  };

  const handleCloseFlagModal = () => {
    setFlagModalOpen(false);
    setFlaggingCommentId(null);
  };

  const handleSubmitFlag = async () => {
    if (!flaggingCommentId) return;
    setFlagLoading(true);
    try {
      await dispatch(
        flagComment({
          commentId: flaggingCommentId,
          flagType,
          specificReason,
        })
      ).unwrap();
      toast.success("Comment flagged successfully.");
      handleCloseFlagModal();
    } catch (err) {
      const errorMsg = err?.message || err?.error || "Failed to flag comment";
      toast.error(errorMsg);
    } finally {
      setFlagLoading(false);
    }
  };

  const handleOpenViewFlagsModal = async (commentId) => {
    setViewFlagsCommentId(commentId);
    setViewFlagsModalOpen(true);
    setFlagsLoading(true);
    try {
      const response = await dispatch(
        fetchCommentFlags({ commentId, page: 0, pageSize: 20 })
      ).unwrap();
      // Assuming response.data is the array or response.content
      setCommentFlags(response?.data?.content || response?.content || []);
    } catch (err) {
      // Backend might return 404 but still include data (quirk)
      const errorData = err?.data || err;
      if (errorData?.content || errorData?.data?.content) {
        setCommentFlags(errorData?.data?.content || errorData?.content || []);
      } else {
        toast.error("Failed to fetch flags.");
      }
    } finally {
      setFlagsLoading(false);
    }
  };

  const handleCloseViewFlagsModal = () => {
    setViewFlagsModalOpen(false);
    setViewFlagsCommentId(null);
    setCommentFlags([]);
  };

  return (
    <Shell>
      <div className="all-the-audios">
        <div className="upper-audio">
          <div className="identity-row">
            <div className="brand-user-row">
              <div className="brand-mark">
                <img
                  src="/images/icon-7.png"
                  alt="Pluglr"
                  style={{ width: "100%", height: "100%" }}
                />
              </div>
              {userImage ? (
                <div className="user-info">
                  <img src={userImage} alt="Host" />
                  <p>
                    {userData?.data?.fname} {userData?.data?.lname}
                  </p>
                </div>
              ) : null}
            </div>
            <div className="title-block">
              <p>{streamTitle}</p>
            </div>
          </div>
          <div className="status-group">
            <div className="audio-upper-middle">
              <div
                className={`red-rounded ${
                  statusVariant === "success" ? "on" : ""
                }`}
              />
              <p>{liveLabel}</p>
              <span>{isPlaying ? "On air" : "Standby"}</span>
            </div>
          </div>
        </div>

        {error ? <p className="error-text">{error}</p> : null}
        {callStatusMessage && (
          <p className="call-status">{callStatusMessage}</p>
        )}

        <div className={`middle-image-box with-controls expanded`}>
          <div
            className="middle-audio-image"
            style={{
              backgroundImage: middleAudioBackground,
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
            }}
          >
            <button
              type="button"
              className="expand-toggle"
              onClick={() => setIsExpanded((prev) => !prev)}
              aria-pressed={isExpanded}
              aria-label={expandToggleLabel}
            >
              <Icon icon={expandToggleIcon} width={22} height={22} />
            </button>

            {autoplayBlocked ? (
              <div className="play-overlay">
                <button
                  type="button"
                  onClick={handleTogglePlayback}
                  disabled={!canControlPlayback}
                  aria-label={playButtonLabel}
                >
                  <Icon
                    icon={isPlaying ? "mdi:pause" : "mdi:play"}
                    width={28}
                    height={28}
                  />
                </button>
              </div>
            ) : null}

            <div className="image-overlay-content">
              <div className="listen-header">
                <span
                  className={`status-pill ${
                    statusVariant === "success" ? "live" : ""
                  }`}
                >
                  <span className="status-dot" />
                  {pillLabel}
                </span>
                {/* <span className="stream-meta">Channel: {channelId || "–"}</span> */}
              </div>

              <div className="listen-play">
                <h2>{streamTitle}</h2>
                <span className="description">{streamDescription}. </span>
                <div className="listners">
                  <Icon icon="octicon:people-24" width={18} height={18} />
                  <span>
                    {isPlaying ? "You are tuned in" : "Waiting for audio"}
                  </span>
                </div>
              </div>

              {/* <div className="support-text">
                {isReady
                  ? "Audio is ready. Use the controls to manage playback or adjust mute settings."
                  : "We’re connecting you to the broadcast. This may take a few moments."}
              </div> */}
            </div>
          </div>
        </div>
      </div>

      <div style={{ width: "100%", maxWidth: "1120px", marginTop: "24px" }}>
        <PanelTabs>
          <PanelActionTab
            $active={panelTab === "controls"}
            onClick={() => setPanelTab("controls")}
          >
            Controls
          </PanelActionTab>
          <PanelActionTab
            $active={panelTab === "chat"}
            onClick={() => setPanelTab("chat")}
          >
            Chat ({comments?.totalElements || 0})
          </PanelActionTab>
        </PanelTabs>

        {panelTab === "controls" && (
          <div className="control-card">
            <div className="control-buttons">
              <button
                type="button"
                className="control-button primary"
                onClick={handleTogglePlayback}
                disabled={!canControlPlayback}
              >
                <Icon
                  icon={isPlaying ? "mdi:pause" : "mdi:play"}
                  width={20}
                  height={20}
                />
                <span className="control-label">{playButtonLabel}</span>
              </button>
              <button
                type="button"
                className="control-button"
                onClick={handleMuteToggle}
                disabled={!isReady}
              >
                <Icon
                  icon={isMuted ? "mdi:volume-off" : "mdi:volume-high"}
                  width={18}
                  height={18}
                />
                <span className="control-label">
                  {isMuted ? "Unmute audio" : "Mute audio"}
                </span>
              </button>
              {isCohost ? (
                <button
                  type="button"
                  className={`control-button ${
                    audienceMuted ? "danger" : "success"
                  }`}
                  onClick={handleAudienceMuteToggle}
                >
                  <Icon
                    icon={
                      audienceMuted ? "mdi:microphone-off" : "mdi:microphone"
                    }
                    width={18}
                    height={18}
                  />
                  <span className="control-label">
                    {audienceMuted ? "Resume mic" : "Mute mic"}
                  </span>
                </button>
              ) : null}
              {!isCohost ? (
                <button
                  type="button"
                  className="control-button tertiary"
                  onClick={handleCallAction}
                  disabled={callButtonDisabled}
                >
                  <Icon icon={callButtonIcon} width={18} height={18} />
                  <span className="control-label">{callButtonLabel}</span>
                </button>
              ) : null}

              {showCallControls ? (
                <button
                  type="button"
                  className={`control-button secondary ${
                    callControlsOpen ? "active" : ""
                  }`}
                  onClick={handleOpenCallControls}
                >
                  <Icon icon="mdi:tune" width={18} height={18} />
                  <span className="control-label">Control</span>
                </button>
              ) : null}
            </div>

            {callStatusMessage ? (
              <p className="call-status">{callStatusMessage}</p>
            ) : null}

            <div className="device-selects">
              <div className="device-select">
                <label htmlFor="listener-mic-select">
                  Microphone for calls
                </label>
                <select
                  id="listener-mic-select"
                  value={selectedMicId}
                  onChange={handleMicSelect}
                  disabled={devicesLoading && audioDevices.inputs.length === 0}
                >
                  <option value="default">System default microphone</option>
                  {audioDevices.inputs.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="device-select">
                <label htmlFor="listener-speaker-select">
                  Speaker for playback
                </label>
                <select
                  id="listener-speaker-select"
                  value={selectedSpeakerId}
                  onChange={handleSpeakerSelect}
                  disabled={
                    !canSelectSpeaker ||
                    (devicesLoading && audioDevices.outputs.length === 0)
                  }
                >
                  <option value="default">System default speaker</option>
                  {audioDevices.outputs.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label}
                    </option>
                  ))}
                </select>
                {!canSelectSpeaker ? (
                  <p className="device-help">
                    Your browser does not support selecting a specific speaker.
                  </p>
                ) : null}
              </div>

              <div className="device-controls-row">
                <button
                  type="button"
                  className="device-refresh"
                  onClick={handleRefreshDevices}
                  disabled={devicesLoading}
                >
                  <Icon icon="mdi:refresh" width={16} height={16} />
                  <span>
                    {devicesLoading ? "Refreshing devices…" : "Refresh devices"}
                  </span>
                </button>
              </div>

              {deviceError ? (
                <p className="device-error">{deviceError}</p>
              ) : null}
              {speakerError ? (
                <p className="device-error">{speakerError}</p>
              ) : null}
            </div>
          </div>
        )}

        {panelTab === "chat" && (
          <ControlPanel>
            <ChatContainer
              ref={chatContainerRef}
              onScroll={handleChatScroll}
              style={{
                height: "600px",
                display: "flex",
                flexDirection: "column",
                padding: "20px",
                overflowY: "auto",
                background: "rgba(255, 255, 255, 0.03)",
                borderRadius: "12px",
                border: "1px solid #272a31",
              }}
            >
              <ControlHeader>
                <h2>Comments</h2>
                <StatusChip $variant="success">Active</StatusChip>
              </ControlHeader>
              {comments?.content?.map((comment, idx) => {
                const commentKey = comment.id || comment._id || idx;
                return (
                  <ChatMessage key={commentKey}>
                    <div className="avatar">
                      <img
                        src={
                          comment.user?.image ||
                          comment.user?.profileImage ||
                          "/images/image_1.png"
                        }
                        alt="User"
                      />
                    </div>
                    <div className="content">
                      <div className="sender">
                        <div className="sender-details">
                          {comment.username}
                          <span className="time">
                            {timeAgo(comment.timestamp)}
                          </span>
                        </div>
                        {isChannelModerator && (
                          <ModeratorMenuButton
                            type="button"
                            onClick={() =>
                              handleToggleModerationMenu(commentKey)
                            }
                            aria-label="Open moderation menu"
                          >
                            <Icon
                              icon="mdi:dots-vertical"
                              width={16}
                              height={16}
                            />
                          </ModeratorMenuButton>
                        )}
                      </div>
                      <div className="text">{comment.text}</div>
                      <ReactionWrapper>
                        <ReactionButton
                          $active={comment.userReaction === "LIKE"}
                          onClick={() =>
                            handleReaction(
                              commentKey,
                              "LIKE",
                              comment.userReaction
                            )
                          }
                          title="Like"
                        >
                          <Icon icon="mdi:thumb-up" width={14} />
                          {comment.reactionCounts?.LIKE > 0 && (
                            <span>{comment.reactionCounts.LIKE}</span>
                          )}
                        </ReactionButton>
                        <ReactionButton
                          $active={comment.userReaction === "DISLIKE"}
                          onClick={() =>
                            handleReaction(
                              commentKey,
                              "DISLIKE",
                              comment.userReaction
                            )
                          }
                          title="Dislike"
                        >
                          <Icon icon="mdi:thumb-down" width={14} />
                          {comment.reactionCounts?.DISLIKE > 0 && (
                            <span>{comment.reactionCounts.DISLIKE}</span>
                          )}
                        </ReactionButton>
                        <ReactionButton
                          $active={comment.userReaction === "LOVE"}
                          onClick={() =>
                            handleReaction(
                              commentKey,
                              "LOVE",
                              comment.userReaction
                            )
                          }
                          title="Love"
                        >
                          <Icon icon="mdi:heart" width={14} />
                          {comment.reactionCounts?.LOVE > 0 && (
                            <span>{comment.reactionCounts.LOVE}</span>
                          )}
                        </ReactionButton>
                        <ReactionButton
                          $active={comment.userReaction === "INDIFFERENT"}
                          onClick={() =>
                            handleReaction(
                              commentKey,
                              "INDIFFERENT",
                              comment.userReaction
                            )
                          }
                          title="Indifferent"
                        >
                          <Icon icon="mdi:emoticon-neutral" width={14} />
                          {comment.reactionCounts?.INDIFFERENT > 0 && (
                            <span>{comment.reactionCounts.INDIFFERENT}</span>
                          )}
                        </ReactionButton>
                        <ReactionButton
                          onClick={() =>
                            handleReaction(
                              commentKey,
                              "NOT_SET",
                              comment.userReaction
                            )
                          }
                          title="Clear"
                        >
                          <Icon icon="mdi:close-circle" width={14} />
                        </ReactionButton>
                        <ReactionButton
                          onClick={() =>
                            handleOpenFlagModal(comment.id || comment._id)
                          }
                          title="Flag Comment"
                        >
                          <Icon icon="mdi:flag-variant" width={14} />
                        </ReactionButton>
                      </ReactionWrapper>
                      {isChannelModerator &&
                        openModerationCommentId === commentKey && (
                          <ModeratorTools>
                            <ModeratorActionSelect
                              value={
                                moderationSelections[commentKey] ?? "NOT_SET"
                              }
                              onChange={(e) =>
                                handleModerationSelectionChange(
                                  commentKey,
                                  e.target.value
                                )
                              }
                            >
                              {MODERATION_ACTIONS.map((action) => (
                                <option key={action} value={action}>
                                  {action.replace(/_/g, " ")}
                                </option>
                              ))}
                            </ModeratorActionSelect>
                            <ModeratorActionButton
                              type="button"
                              style={{
                                backgroundColor: "#4B5563",
                              }}
                              onClick={() =>
                                handleOpenViewFlagsModal(
                                  comment.id || comment._id
                                )
                              }
                            >
                              <Icon
                                icon="mdi:flag"
                                width={14}
                                style={{ marginRight: "4px" }}
                              />
                              View Flags
                            </ModeratorActionButton>
                            <ModeratorReasonInput
                              type="text"
                              placeholder="Reason (optional)"
                              value={moderationReasons[commentKey] ?? ""}
                              onChange={(e) =>
                                handleModerationReasonChange(
                                  commentKey,
                                  e.target.value
                                )
                              }
                            />
                            <ModeratorActionButton
                              type="button"
                              onClick={() => handleModerationSubmit(comment)}
                              disabled={
                                moderationLoading[commentKey] ||
                                (moderationSelections[commentKey] ??
                                  "NOT_SET") === "NOT_SET"
                              }
                            >
                              {moderationLoading[commentKey]
                                ? "Applying..."
                                : "Apply"}
                            </ModeratorActionButton>
                          </ModeratorTools>
                        )}
                    </div>
                  </ChatMessage>
                );
              })}
              {commentsLoading && (
                <div style={{ textAlign: "center", padding: "10px" }}>
                  <ClipLoader size={20} color="#ed1c24" />
                </div>
              )}

              {!commentsHasMore && (comments?.content?.length || 0) > 0 && (
                <EmptyState>End of comments</EmptyState>
              )}

              {!commentsLoading && (comments?.content?.length || 0) === 0 && (
                <EmptyState>No comments yet. Be the first!</EmptyState>
              )}
            </ChatContainer>

            <ChatInputWrapper>
              <ChatInput
                type="text"
                placeholder="Type a message..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSendComment();
                  }
                }}
              />
              <ChatSendButton
                type="button"
                disabled={!commentText.trim()}
                onClick={handleSendComment}
              >
                <Icon icon="mdi:send" width={20} height={20} />
              </ChatSendButton>
            </ChatInputWrapper>
          </ControlPanel>
        )}
      </div>
      <audio ref={audioRef} autoPlay playsInline style={{ display: "none" }} />
      <audio
        ref={monitorAudioRef}
        autoPlay
        playsInline
        muted={!selfMonitorEnabled}
        style={{ display: "none" }}
      />

      {showCallControls && callControlsOpen ? (
        <ControlsModalBackdrop
          role="dialog"
          aria-modal="true"
          onClick={handleCloseCallControls}
        >
          <ControlsModalCard onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3>Live call controls</h3>
              <button
                type="button"
                onClick={handleCloseCallControls}
                aria-label="Close call controls"
              >
                <Icon icon="mdi:close" width={20} height={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="call-controls">
                <div className="control-row">
                  <label htmlFor="audience-level-user">
                    Audience send level
                  </label>
                  <input
                    id="audience-level-user"
                    type="range"
                    min="0"
                    max="100"
                    value={audienceLevel}
                    onChange={handleAudienceVolumeChange}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={audienceLevel}
                  />
                  <button
                    type="button"
                    className="control-button small"
                    onClick={handleAudienceMuteToggle}
                  >
                    <Icon
                      icon={
                        audienceMuted ? "mdi:microphone-off" : "mdi:microphone"
                      }
                      width={18}
                      height={18}
                    />
                    {audienceMuted ? "Audience muted" : "Audience live"}
                  </button>
                </div>

                <div className="control-row">
                  <label htmlFor="monitor-toggle-user">
                    <input
                      id="monitor-toggle-user"
                      type="checkbox"
                      checked={selfMonitorEnabled}
                      onChange={handleSelfMonitorToggle}
                    />
                    Hear yourself
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={selfMonitorLevel}
                    onChange={handleSelfMonitorVolumeChange}
                    disabled={!selfMonitorEnabled}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={selfMonitorLevel}
                  />
                </div>

                <div className="control-row">
                  <label>
                    <Icon icon="mdi:volume-high" width={16} />
                    Stream Volume ({Math.round(audienceLevel)}%)
                  </label>
                </div>

                {!isCohost ? (
                  <div className="control-row">
                    <button
                      type="button"
                      className="control-button danger"
                      onClick={handleEndCall}
                      disabled={!isCallLive}
                    >
                      <Icon icon="mdi:phone-hangup" width={18} height={18} />
                      End call
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </ControlsModalCard>
        </ControlsModalBackdrop>
      ) : null}

      {/* Moderation Success Modal */}
      {moderationSuccessMessage && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10000,
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              width: "400px",
              maxWidth: "90%",
              padding: "30px",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "20px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                background: "#d1fae5",
                color: "#059669",
                width: "60px",
                height: "60px",
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginBottom: "10px",
              }}
            >
              <Icon icon="mdi:check-circle" width="40" height="40" />
            </div>
            <h3
              style={{
                fontSize: "22px",
                fontWeight: "600",
                color: "#060b13",
                margin: 0,
              }}
            >
              Success
            </h3>
            <p
              style={{
                fontSize: "16px",
                color: "#475467",
                lineHeight: "1.5",
                margin: 0,
              }}
            >
              {moderationSuccessMessage}
            </p>
            <button
              onClick={() => setModerationSuccessMessage(null)}
              style={{
                background: "#059669",
                color: "white",
                border: "none",
                padding: "12px 30px",
                borderRadius: "100px",
                fontSize: "16px",
                fontWeight: "600",
                cursor: "pointer",
                width: "100%",
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Moderation Error Modal */}
      {moderationErrorMessage && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10000,
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              width: "400px",
              maxWidth: "90%",
              padding: "30px",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "20px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                background: "#fee2e2",
                color: "#dc2626",
                width: "60px",
                height: "60px",
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginBottom: "10px",
              }}
            >
              <Icon icon="mdi:alert-circle" width="40" height="40" />
            </div>
            <h3
              style={{
                fontSize: "22px",
                fontWeight: "600",
                color: "#060b13",
                margin: 0,
              }}
            >
              Error
            </h3>
            <p
              style={{
                fontSize: "16px",
                color: "#475467",
                lineHeight: "1.5",
                margin: 0,
              }}
            >
              {moderationErrorMessage}
            </p>
            <button
              onClick={() => setModerationErrorMessage(null)}
              style={{
                background: "#ed1c24",
                color: "white",
                border: "none",
                padding: "12px 30px",
                borderRadius: "100px",
                fontSize: "16px",
                fontWeight: "600",
                cursor: "pointer",
                width: "100%",
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Flag Comment Modal */}
      {flagModalOpen && (
        <ControlsModalBackdrop onClick={handleCloseFlagModal}>
          <ControlsModalCard onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Flag Comment</h3>
              <button type="button" onClick={handleCloseFlagModal}>
                <Icon icon="mdi:close" width={20} height={20} />
              </button>
            </div>
            <div className="modal-body">
              <label style={{ color: "#c5c6d0", fontSize: "14px" }}>
                Reason
              </label>
              <ModeratorActionSelect
                value={flagType}
                onChange={(e) => setFlagType(e.target.value)}
                style={{ width: "100%" }}
              >
                <option value="HARRASMENT_AND_BULLYING">
                  Harassment and Bullying
                </option>
                <option value="SPAM">Spam</option>
                <option value="INAPPROPRIATE_CONTENT">
                  Inappropriate Content
                </option>
                <option value="HATE_SPEECH">Hate Speech</option>
                <option value="OTHER">Other</option>
              </ModeratorActionSelect>

              <label style={{ color: "#c5c6d0", fontSize: "14px" }}>
                Specific Reason (Optional)
              </label>
              <ModeratorReasonInput
                value={specificReason}
                onChange={(e) => setSpecificReason(e.target.value)}
                placeholder="Details..."
                style={{ width: "100%" }}
              />

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "8px",
                  marginTop: "16px",
                }}
              >
                <button
                  onClick={handleCloseFlagModal}
                  style={{
                    background: "transparent",
                    color: "#c5c6d0",
                    border: "1px solid #333",
                    padding: "8px 16px",
                    borderRadius: "8px",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitFlag}
                  disabled={flagLoading}
                  style={{
                    background: "#ed1c24",
                    color: "white",
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: "8px",
                    cursor: flagLoading ? "not-allowed" : "pointer",
                    opacity: flagLoading ? 0.7 : 1,
                  }}
                >
                  {flagLoading ? "Flagging..." : "Submit Flag"}
                </button>
              </div>
            </div>
          </ControlsModalCard>
        </ControlsModalBackdrop>
      )}

      {/* View Flags Modal (Moderator) */}
      {viewFlagsModalOpen && (
        <ControlsModalBackdrop onClick={handleCloseViewFlagsModal}>
          <ControlsModalCard
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "600px" }}
          >
            <div className="modal-header">
              <h3>Flag Reports</h3>
              <button type="button" onClick={handleCloseViewFlagsModal}>
                <Icon icon="mdi:close" width={20} height={20} />
              </button>
            </div>
            <div
              className="modal-body"
              style={{ maxHeight: "400px", overflowY: "auto" }}
            >
              {flagsLoading ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    color: "#c5c6d0",
                  }}
                >
                  Loading flags...
                </div>
              ) : commentFlags.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    color: "#c5c6d0",
                  }}
                >
                  No flags found for this comment.
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  {commentFlags.map((flag, idx) => (
                    <div
                      key={idx}
                      style={{
                        background: "rgba(255,255,255,0.05)",
                        padding: "12px",
                        borderRadius: "8px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "4px",
                        }}
                      >
                        <span
                          style={{
                            color: "#ed1c24",
                            fontWeight: "bold",
                            fontSize: "13px",
                          }}
                        >
                          {flag.flagType?.replace(/_/g, " ")}
                        </span>
                        <span style={{ color: "#8f9099", fontSize: "11px" }}>
                          {timeAgo(flag.timestamp || flag.createdAt)}
                        </span>
                      </div>
                      {flag.specificReason && (
                        <p
                          style={{
                            color: "#eff0fa",
                            fontSize: "13px",
                            margin: 0,
                          }}
                        >
                          {flag.specificReason}
                        </p>
                      )}
                      <div
                        style={{
                          marginTop: "4px",
                          fontSize: "11px",
                          color: "#666",
                        }}
                      >
                        Reported by: {flag.reporterUsername || "User"}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </ControlsModalCard>
        </ControlsModalBackdrop>
      )}
    </Shell>
  );
};

const ControlsModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 1100;
`;

const ControlsModalCard = styled.div`
  width: 100%;
  max-width: 420px;
  background: #101116;
  border-radius: 18px;
  border: 1px solid #2b2d35;
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.5);

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .modal-header h3 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #f2f4ff;
  }

  .modal-header button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 12px;
    border: 1px solid #2f313a;
    background: rgba(255, 255, 255, 0.04);
    color: #f2f4ff;
    cursor: pointer;
    transition: background 0.2s ease, transform 0.15s ease;
  }

  .modal-header button:hover {
    background: rgba(255, 255, 255, 0.08);
    transform: translateY(-1px);
  }

  .modal-body {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }
`;

const PanelTabs = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 999px;
  background: #1c1c1c;
  margin-bottom: 20px;
`;

const PanelActionTab = styled.button`
  border: none;
  border-radius: 999px;
  padding: 8px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: ${({ $active }) => ($active ? "#ed1c24" : "transparent")};
  color: ${({ $active }) => ($active ? "#ffffff" : "#c5c6d0")};
  transition: all 0.2s ease;

  &:hover {
    background: ${({ $active }) => ($active ? "#ed1c24" : "#2d2d2d")};
    color: #ffffff;
  }
`;

const ControlPanel = styled.div`
  background: #111111;
  border: 1px solid #272a31;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ControlHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  position: sticky;
  top: -20px;
  background: #111111;
  z-index: 10;
  padding: 12px 0 16px;
  border-bottom: 1px solid #272a31;
  margin-bottom: 16px;
  width: 100%;

  h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #eff0fa;
  }
`;

const ControlContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const ChatContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.02);
  min-height: 400px;
  max-height: 600px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #333;
    border-radius: 10px;
  }
`;

const ChatMessage = styled.div`
  display: flex;
  gap: 12px;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    overflow: hidden;
    background: #222;
    flex-shrink: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  .sender {
    font-size: 13px;
    font-weight: 600;
    color: #e4edff;
    display: flex;
    align-items: center;
    gap: 8px;

    .time {
      font-size: 11px;
      font-weight: 400;
      color: #8f9099;
    }
  }

  .text {
    font-size: 14px;
    line-height: 1.5;
    color: #eff0fa;
    word-break: break-word;
  }
`;

const ModeratorMenuButton = styled.button`
  border: none;
  background: transparent;
  color: #8f9099;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  padding: 4px;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: #ffffff;
  }
`;

const ModeratorTools = styled.div`
  margin-top: 12px;
  padding: 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const ModeratorActionSelect = styled.select`
  background: #111;
  border: 1px solid #333;
  color: #eff0fa;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 12px;
  min-width: 180px;
  outline: none;

  option {
    background: #111;
  }
`;

const ModeratorReasonInput = styled.input`
  flex: 1;
  min-width: 180px;
  border-radius: 8px;
  border: 1px solid #333;
  background: #0f0f0f;
  color: #eff0fa;
  padding: 8px 10px;
  font-size: 12px;
  outline: none;

  &::placeholder {
    color: #6c6d75;
  }
`;

const ModeratorActionButton = styled.button`
  border: none;
  border-radius: 8px;
  background: #ed1c24;
  color: white;
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ReactionWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
`;

const ReactionButton = styled.button`
  background: ${({ $active }) =>
    $active ? "rgba(237, 28, 36, 0.15)" : "#222"};
  border: 1px solid ${({ $active }) => ($active ? "#ed1c24" : "#333")};
  color: ${({ $active }) => ($active ? "#ed1c24" : "#8f9099")};
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #ed1c24;
    color: #ed1c24;
  }

  span {
    font-weight: 600;
  }
`;

const ChatInputWrapper = styled.div`
  display: flex;
  gap: 10px;
  padding: 12px;
  background: #1b1b1b;
  border-radius: 12px;
  border: 1px solid #272a31;
`;

const ChatInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  color: #eff0fa;
  font-size: 14px;
  outline: none;

  &::placeholder {
    color: #666;
  }
`;

const ChatSendButton = styled.button`
  background: #ed1c24;
  color: white;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #ff2c34;
    transform: scale(1.05);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.p`
  font-size: 14px;
  color: #8f9099;
  text-align: center;
  margin: 20px 0;
`;

const StatusChip = styled.span`
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: ${({ $variant }) =>
    $variant === "success"
      ? "rgba(34, 197, 94, 0.15)"
      : "rgba(237, 28, 36, 0.15)"};
  color: ${({ $variant }) => ($variant === "success" ? "#4ade80" : "#ff4d4d")};
  border: 1px solid
    ${({ $variant }) =>
      $variant === "success"
        ? "rgba(34, 197, 94, 0.3)"
        : "rgba(237, 28, 36, 0.3)"};
`;

const ControlSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

export default UserListener;

const Shell = styled.div`
  min-height: 100vh;
  background: #000000;
  color: #eff0fa;
  padding: 32px 16px 64px;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;

  .all-the-audios {
    width: 100%;
    max-width: 1120px;
    display: flex;
    flex-direction: column;
    gap: 32px;
  }

  .upper-audio {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    flex-wrap: wrap;
  }

  .identity-row,
  .status-group {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
  }

  .identity-row {
    flex: 1 1 auto;
    min-width: 240px;
    gap: 20px;
  }

  .brand-user-row {
    display: inline-flex;
    align-items: center;
    gap: 14px;
  }

  .status-group {
    flex: 0 1 auto;
    justify-content: flex-end;
  }

  .user-info {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    border-radius: 999px;
    background: #242424;
    color: #f5f5f5;
    line-height: 1;
  }

  .user-info img {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    object-fit: cover;
  }

  .user-info p {
    margin: 0;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
  }

  .brand-mark {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: #1b1b1b;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px;
    border: 1px solid #272a31;
  }

  .title-block {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 8px 14px;
    border-radius: 999px;
    background: #242424;
  }

  .title-block p {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #e9e9ea;
    letter-spacing: 0.01em;
  }
  .title-block span {
    display: block;
    margin-top: 4px;
    font-size: 13px;
    color: #c5c6d0;
  }

  .audio-upper-middle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border: 1px solid #272a31;
    border-radius: 999px;
    padding: 10px 16px;
    background: #1b1b1b;
  }

  .red-rounded {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #828286;
    transition: background 0.2s ease, box-shadow 0.2s ease;
  }

  .red-rounded.on {
    background: #ed1c24;
    box-shadow: 0 0 12px rgba(237, 28, 36, 0.65);
  }

  .audio-upper-middle p {
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .audio-upper-middle span {
    color: #8f9099;
    font-size: 12px;
  }

  .status-chip {
    padding: 8px 16px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 500;
    background: rgba(59, 130, 246, 0.12);
    color: #93c5fd;
  }

  .status-chip.success {
    background: rgba(34, 197, 94, 0.18);
    color: #4ade80;
  }

  .status-chip.error {
    background: rgba(248, 113, 113, 0.18);
    color: #fca5a5;
  }

  .error-text {
    font-size: 12px;
    color: #f87171;
    margin: 0;
  }

  .call-status {
    font-size: 12px;
    color: #93c5fd;
    margin: 0;
  }

  .middle-image-box {
    background: #1b1b1b;
    border-radius: 20px;
    padding: 32px;
    display: flex;
    gap: 32px;
    align-items: stretch;
    position: relative;
    overflow: hidden;
    transition: flex-direction 0.25s ease, padding 0.25s ease;
  }

  .middle-image-box.with-controls {
    flex-direction: row;
  }

  .middle-image-box.expanded {
    flex-direction: column;
    padding: 40px 32px 48px;
    gap: 40px;
  }

  .middle-image-box.expanded .middle-audio-image {
    min-height: 620px;
  }

  .middle-image-box.expanded .controls-column {
    max-width: none;
    width: 100%;
    margin: 0;
    padding: 0;
    border: none;
    background: transparent;
    align-items: center;
  }

  .middle-image-box.expanded .controls-column .control-card {
    width: 100%;
    max-width: 360px;
    border: none;
    background: transparent;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: center;
  }

  .middle-image-box.expanded .controls-column .control-buttons {
    flex-direction: row;
    justify-content: center;
    width: 100%;
  }

  .middle-image-box.expanded .controls-column .control-button.primary,
  .middle-image-box.expanded .controls-column .fine-print,
  .middle-image-box.expanded .controls-column .native-audio {
    display: none;
  }

  .middle-image-box.expanded .controls-column .control-button {
    min-width: 180px;
  }

  .middle-image-box.expanded .controls-column .control-sub {
    display: none;
  }

  .middle-audio-image {
    flex: 1;
    min-height: 360px;
    border-radius: 16px;
    background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0) 32%,
        rgba(0, 0, 0, 0.25) 55%,
        rgba(0, 0, 0, 0.55) 75%,
        rgba(0, 0, 0, 0.92) 100%
      ),
      linear-gradient(0deg, rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0.35)),
      url("/images/audio-live-bg.png");
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    padding: 32px;
    position: relative;
    color: #ffffff;
    overflow: hidden;
  }

  .image-overlay-content {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 32px;
    height: 100%;
  }

  .listen-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .status-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 16px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    background: rgba(59, 130, 246, 0.32);
    color: #e4edff;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    text-shadow: 0 6px 20px rgba(0, 0, 0, 0.6);
  }

  .status-pill.live {
    background: rgba(237, 28, 36, 0.32);
    color: #ffd6d6;
  }

  .status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
  }

  .stream-meta {
    font-size: 12px;
    color: #f1f1f1;
    background: rgba(23, 23, 23, 0.65);
    padding: 6px 12px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    text-shadow: 0 6px 20px rgba(0, 0, 0, 0.6);
  }

  .listen-play {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .listen-play h2 {
    margin: 0;
    font-size: 28px;
    font-weight: 600;
    color: #ffffff;
    text-shadow: 0 8px 28px rgba(0, 0, 0, 0.65);
  }

  .listen-play span.description {
    font-size: 14px;
    color: #f4f4f4;
    max-width: 420px;
    line-height: 1.65;
    text-shadow: 0 6px 22px rgba(0, 0, 0, 0.56);
  }

  .listners {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #ffffff;
    font-size: 13px;
    font-weight: 500;
    text-shadow: 0 6px 22px rgba(0, 0, 0, 0.56);
  }

  .support-text {
    color: rgba(237, 237, 237, 0.8);
    font-size: 12px;
    max-width: 420px;
    line-height: 1.65;
    text-shadow: 0 6px 22px rgba(0, 0, 0, 0.56);
  }

  .play-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }

  .play-overlay button {
    pointer-events: auto;
    width: 72px;
    height: 72px;
    border-radius: 50%;
    border: none;
    background: rgba(237, 28, 36, 0.92);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 16px 40px rgba(237, 28, 36, 0.45);
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }

  .play-overlay button:hover {
    transform: translateY(-2px);
    box-shadow: 0 20px 50px rgba(237, 28, 36, 0.55);
  }

  .expand-toggle {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 2;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.18);
    background: rgba(17, 17, 17, 0.85);
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: transform 0.2s ease, background 0.2s ease;
  }

  .expand-toggle:hover {
    transform: translateY(-1px);
    background: rgba(17, 17, 17, 0.95);
  }

  .controls-column {
    width: 100%;
    max-width: 340px;
    border-radius: 16px;
    border: 1px solid #272a31;
    background: #111111;
    padding: 24px;
    display: flex;
    justify-content: space-between;
    gap: 20px;
  }

  .control-card h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #eff0fa;
  }

  .control-sub {
    margin: 6px 0 0;
    font-size: 13px;
    color: #c5c6d0;
  }

  .device-selects {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 8px;
  }

  .device-selects .device-select {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .device-selects .device-select label {
    font-size: 13px;
    font-weight: 500;
    color: #c5c6d0;
  }

  .device-selects .device-select select {
    border-radius: 10px;
    border: 1px solid #2f3035;
    background: #1b1b1d;
    color: #eff0fa;
    padding: 10px 12px;
    font-size: 14px;
  }

  .device-selects .device-select select:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .device-controls-row {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
  }

  .device-controls-row .device-refresh {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border: 1px solid #272a31;
    border-radius: 10px;
    background: #1b1b1b;
    color: #eff0fa;
    padding: 8px 12px;
    font-size: 13px;
    cursor: pointer;
  }

  .device-controls-row .device-refresh:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .device-selects .device-help {
    font-size: 12px;
    color: #8d8f99;
    margin: 0;
  }

  .device-error {
    color: #fca5a5;
    font-size: 12px;
    margin: 6px 0 0;
  }

  .control-buttons {
    display: flex;
    width: 100%;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 24px 0 12px;
  }

  .call-controls-trigger {
    position: relative;
    display: inline-flex;
  }

  .call-controls-trigger > .control-button {
    min-width: 140px;
    justify-content: space-between;
  }

  .call-controls-trigger.open > .control-button {
    background: #232323;
    border-color: #32343b;
  }

  .call-controls-dropdown {
    position: absolute;
    top: calc(100% + 10px);
    right: 0;
    width: 320px;
    background: #111111;
    border: 1px solid #272a31;
    border-radius: 14px;
    box-shadow: 0 18px 45px rgba(0, 0, 0, 0.4);
    padding: 20px;
    z-index: 15;
  }

  .call-controls {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  .call-controls h4 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #eff0fa;
  }

  .call-controls .control-row {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .call-controls .control-row label {
    font-size: 13px;
    font-weight: 500;
    color: #c5c6d0;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .call-controls .control-row input[type="range"] {
    width: 100%;
  }

  .call-controls .control-row .control-button.small {
    align-self: flex-start;
  }

  .control-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    border: 1px solid #272a31;
    border-radius: 12px;
    padding: 12px 18px;
    font-size: 14px;
    font-weight: 600;
    background: #1b1b1b;
    color: #eff0fa;
    cursor: pointer;
    transition: transform 0.15s ease, background 0.2s ease,
      border-color 0.2s ease;
  }

  .control-button.primary {
    background: #ed1c24;
    border-color: #ed1c24;
    color: #ffffff;
  }

  .control-button:hover:not(:disabled) {
    transform: translateY(-1px);
    background: #232323;
  }

  .control-button.primary:hover:not(:disabled) {
    background: #ff2c34;
    border-color: #ff2c34;
  }

  .control-button:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
  }

  .control-button.secondary {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.12);
    color: #eff0fa;
  }

  .control-button.secondary.active {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.2);
  }

  .native-audio {
    width: 300px;
    border-radius: 12px;
    background: #000000;
    border: 1px solid #272a31;
    padding: 8px 12px;
  }

  .fine-print {
    margin: 0;
    font-size: 12px;
    color: #8d8f99;
    line-height: 1.6;
  }

  @media (max-width: 1024px) {
    .middle-image-box {
      flex-direction: column;
    }

    .controls-column {
      max-width: none;
      width: 100%;
    }
  }

  @media (max-width: 640px) {
    .middle-image-box {
      padding: 24px;
      gap: 24px;
    }

    .middle-audio-image {
      padding: 24px;
    }

    .middle-image-box {
      padding: 2px !important;
      background: transparent !important;
    }
    .middle-image-box.expanded .middle-audio-image {
      min-height: 400px !important;
      box-shadow: 0 0 12px 4px rgba(255, 255, 255, 0.55),
        0 0 25px 10px rgba(0, 150, 255, 0.45);
    }

    .upper-audio {
      flex-direction: column;
      align-items: stretch;
    }

    .identity-row {
      width: 100%;
      justify-content: space-between;
    }

    .brand-user-row {
      flex: 1 1 auto;
      justify-content: flex-start;
    }

    .title-block {
      margin-top: 12px;
    }

    .status-group {
      width: 100%;
      justify-content: center;
    }

    .control-buttons {
      justify-content: center;
      gap: 10px;
    }

    .control-button {
      width: 54px;
      height: 54px;
      padding: 0;
      border-radius: 50%;
    }

    .control-button .control-label {
      display: none;
    }

    .listen-play h2 {
      font-size: 22px;
    }

    .play-overlay button {
      width: 60px;
      height: 60px;
    }
  }
`;
